import { createSelector } from '@ngrx/store';

import { MakeRef } from '../types';
import { createActionsWithReducer } from '../utils';
import { createEntityAdapter } from './adapter';
import {
  EntityCollectionStoreOptions,
  EntityCollectionStoreCustomActions,
  GenericEntity,
  EntityState,
  GenericMetaOptions,
  ID,
  IdSelectorOrDefault,
} from './types';

// eslint-disable-next-line max-lines-per-function
export function createEntityCollectionStore<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions = GenericMetaOptions,
>() {
  // eslint-disable-next-line max-lines-per-function
  return <
    StoreOptions extends EntityCollectionStoreOptions<Entity, MetaOptions>,
    NamespaceType extends string,
  >(
    namespace: NamespaceType,
    options?: StoreOptions,
  ) => {
    const entityCollectionNamespace: `entityCollection/${NamespaceType}` = `entityCollection/${namespace}`;
    const baseSelector =
      options?.baseSelector ||
      createSelector(
        (root: any) => <EntityState<Entity>>root[entityCollectionNamespace],
        state => state,
      );
    // @ts-ignore
    const idSelector: IdSelectorOrDefault<StoreOptions['idSelector'], Entity> =
      options?.idSelector || ((entity: { id: ID }) => entity.id);

    const makeRef: MakeRef<Entity> = (mapFrom, mapTo, source) => ({ mapFrom, mapTo, source });
    const {
      adapter,
      initialState: defaultInitState,
      selectors,
      defaultActionConfig,
    } = createEntityAdapter<
      Entity,
      IdSelectorOrDefault<StoreOptions['idSelector'], Entity>,
      MetaOptions
    >(baseSelector, entityCollectionNamespace, idSelector, options?.references?.(makeRef));
    const initialState = options?.initialState || defaultInitState;
    const mergedActions = <
      typeof defaultActionConfig & EntityCollectionStoreCustomActions<StoreOptions>
    >{
      ...defaultActionConfig,
      ...(options?.customActions?.(adapter) || {}),
    };

    const { actions, reducer } = createActionsWithReducer(namespace, initialState, mergedActions);
    return {
      namespace: entityCollectionNamespace,
      selectors,
      actions,
      reducer,
      initialState,
    };
  };
}
